import './ShipmentShow.scss';

import { DateField, Show, TextField } from '@refinedev/antd';
import { useOne, useResourceParams, useShow } from '@refinedev/core';
import { Card, Tag } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';
import { Ticket } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import ShipmentShowAddress from './ShipmentShow/ShipmentShowAddress';
import ShipmentShowDetail from './ShipmentShow/ShipmentShowDetail';
import ShipmentShowHeaderButtons from './ShipmentShow/ShipmentShowHeaderButtons';
import ShipmentShowLogistic from './ShipmentShow/ShipmentShowLogistic';
import ShipmentShowPayment from './ShipmentShow/ShipmentShowPayment';

export const ShipmentShow = () => {
  const { t } = useTranslation();
  const { packageItemRepository } = useRepositoriesContext();
  const { resource, id } = useResourceParams();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const [packageItems, setPackageItems] = useState<PackageItem[]>([]);
  const [blockChangeState, setBlockChangeState] = useState<{ id: string; message: string }[]>([]);
  const {
    result,
    query: { isLoading, refetch },
  } = useShow<Shipment>({
    resource: RESOURCES.SHIPMENTS,
    id: id,
  });
  const {
    result: ticketResult,
    query: { isLoading: ticketIsLoading },
  } = useOne<Ticket>({
    resource: RESOURCES.TICKETS,
    id: result?.ticket || '',
    queryOptions: {
      enabled: !!result && !!result.ticket,
    },
  });
  const {
    result: createdByResult,
    query: { isLoading: createdByIsLoading },
  } = useOne({
    resource: 'users',
    id: result?.created_by || '',
    queryOptions: {
      enabled: !!result && !!result.created_by,
      retry: false,
    },
  });
  const {
    result: technicianResult,
    query: { isLoading: technicianIsLoading },
  } = useOne({
    resource: 'empresas',
    id: result?.technician || '',
    queryOptions: {
      enabled: !!result && !!result.technician,
      retry: false,
    },
  });
  const loading = useMemo(
    () =>
      isLoading ||
      ticketIsLoading ||
      createdByIsLoading ||
      (!!result?.technician && technicianIsLoading),
    [isLoading, ticketIsLoading, createdByIsLoading, result?.technician, technicianIsLoading],
  );
  const itemsEditMode = useMemo(
    () =>
      hasPermission('edit')
        ? result?.status == PACKAGE_STATES.PENDIENTE
          ? 'enabled'
          : userWorkArea == USER_WORK_AREA.LOGISTICS &&
              (result?.status == PACKAGE_STATES.PREPARACION ||
                result?.status == PACKAGE_STATES.PREPARADO)
            ? 'partial-with-news'
            : 'disabled'
        : 'disabled',
    [hasPermission, result?.status, userWorkArea],
  );
  const canSend = useMemo(
    () =>
      packageItems.every(item =>
        item.product_type == PRODUCT_TYPES.UNIT
          ? item.execute_amount == 0 || item.unidad != undefined
          : item.product_type == PRODUCT_TYPES.DEVICE
            ? item.execute_amount == 0 || item.device != undefined
            : true,
      ),
    [packageItems],
  );
  const isPaid = useMemo(
    () =>
      packageItems
        .filter(item => item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO)
        .every(item => item.billing_number),
    [packageItems],
  );
  const collectionShow = useMemo(
    () => resource?.name === RESOURCES.SPARE_PARTS_COLLECTIONS,
    [resource],
  );
  const showQuantityAlert = useMemo(() => {
    if (collectionShow) return;

    return packageItems.some(item => item.execute_amount == undefined || item.execute_amount == 0);
  }, [collectionShow, packageItems]);
  const refetchResource = useCallback(async () => {
    void refetch().then(() => {
      if (result?.id)
        void packageItemRepository
          .getPackageItemsFromPackages([result.id])
          .then(pi => setPackageItems(pi));
    });
  }, [packageItemRepository, refetch, result?.id]);

  return (
    <Show
      title={
        collectionShow
          ? t('Venta de repuestos envío') + ' #' + id
          : t('Detalles del envío') + ' #' + id
      }
      isLoading={loading}
      headerButtons={buttonProps =>
        result && (
          <ShipmentShowHeaderButtons
            record={result}
            refineButtonsProps={buttonProps}
            refetch={() => void refetchResource()}
            blockChangeState={blockChangeState}
            canSend={canSend}
            isPaid={isPaid}
            showQuantityAlert={showQuantityAlert}
          />
        )
      }>
      <div className="ShipmentShow">
        <div className="ShipmentShowDataRow">
          <div className="ShipmentShowData ShipmentShowDataId">
            <b>
              {t('Id de envío')}: {result?.id}
            </b>
            {result?.status != undefined && (
              <div className="TicketShowRowBooleans">
                <PackageStateTag packageStatus={result.status} />
                {result?.tipo == SHIPMENT_TYPE.AUTOMATICO && (
                  <Tag color="geekblue">{t('Automático')}</Tag>
                )}
              </div>
            )}
          </div>

          <div className="ShipmentShowData">
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={result?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={result?.updated_at} />
            </div>

            <div>
              <b>{t('Creado por')}: </b>
              <TextField
                value={
                  createdByResult
                    ? `${createdByResult?.first_name} ${createdByResult?.last_name} (${createdByResult?.id})`
                    : ''
                }
              />
            </div>
          </div>
        </div>
        {result?.status == PACKAGE_STATES.CANCELADO && result?.cancellation_motive && (
          <Card className="ShipmentShowSection">
            <div className="ShipmentShowData">
              <b>{t('Motivo de cancelación')}</b>
              <TextField value={result?.cancellation_motive} />
            </div>
          </Card>
        )}
        <Card className="ShipmentShowSection">
          <div className="ShipmentShowTitle">{t('Detalles de facturación')}</div>
          <ShipmentShowPayment
            record={result}
            packageItems={packageItems}
            refetch={refetchResource}
          />
        </Card>
        <Card className="ShipmentShowSection">
          <div className="ShipmentShowTitle">{t('Detalles del envío')}</div>
          <ShipmentShowDetail record={result} ticket={ticketResult} />
        </Card>

        <Card className="ShipmentShowSection">
          <div className="ShipmentShowTitle">{t('Dirección de envío')}</div>
          <ShipmentShowAddress record={result} />
        </Card>
        <Card className="ShipmentShowSection">
          <div className="ShipmentShowData">
            <b>{t('Técnico involucrado')}</b>
            {result?.technician && technicianResult ? (
              <LinkTextField
                label={`${technicianResult?.full_name} (${technicianResult?.id})`}
                to={`${COMPANY_URLS.ADMIN_EMPRESA}/${technicianResult?.id}`}
                admin
              />
            ) : (
              <span>{t('No habrá técnico involucrado')}</span>
            )}
          </div>
        </Card>
        <Card className="ShipmentShowSection">
          <div className="ShipmentShowTitle">{t('Logística')}</div>
          <ShipmentShowLogistic record={result} refetch={() => void refetchResource()} />
        </Card>

        <div className="ShipmentShowTitle">{t('Elementos')}</div>
        <PackageItemsList
          packageItems={packageItems}
          setPackageItems={setPackageItems}
          editMode={itemsEditMode}
          package={{ id: result?.id }}
          setBlockChangeState={setBlockChangeState}
          refetchResource={() => void refetchResource()}
        />
      </div>
    </Show>
  );
};
